import { useState } from 'react';
import PropTypes from 'prop-types';
import Loading from 'Components/Atoms/Loading/Loading';
import {
    createProviderConnectionWithWidgetStripe,
    createProviderConnectionWithWidgetStripeConfirm,
} from 'Services/providersConnections';
import Button from 'Components/Atoms/Button/Button';
import { STRIPE_PK } from 'config';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useTranslation } from 'react-i18next';

function loadScript(src, id) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.id = id;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

function removeScript(id) {
    const script = document.getElementById(id);
    if (script) {
        script.remove();
    }
}

const WIDGET_URL = 'https://js.stripe.com/v3/';

export default function ProviderConnectionConnectWithWidgetStripe(props) {
    const { providerConnectionId, connectorId, providerName, providerLogoUrl, onCancel, onConnect } = props;
    const { showUnknownError } = useErrors();

    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation('common');

    const confirmConnection = async (token) => {
        let connectionId = await createProviderConnectionWithWidgetStripeConfirm({
            connectorId,
            providerConnectionId,
            token,
            name: providerName,
        });

        onConnect(connectionId);
    };

    const initialize = async () => {
        setIsLoading(true);

        try {
            await loadScript(WIDGET_URL, 'stripe-widget');

            let { token, enrollment_id } = await createProviderConnectionWithWidgetStripe({
                connectorId,
                providerConnectionId,
                name: providerName,
            });

            const stripe = window.Stripe(STRIPE_PK);

            stripe.collectFinancialConnectionsAccounts({ clientSecret: token }).then(function (result) {
                if (result.financialConnectionsSession.accounts.length > 0) {
                    confirmConnection(enrollment_id);
                } else {
                    removeScript();
                    onCancel();
                }
            });
        } catch (error) {
            showUnknownError(error);
            removeScript();
            onCancel();
        }
    };

    return (
        <div className="text-center p-6">
            <div className="flex items-center justify-center">
                <img src={providerLogoUrl} alt={providerName} className="w-16 h-auto block" />
                <h2 className="mb-0 text-2xl md:text-3xl text-pretty font-semibold">{providerName}</h2>
            </div>

            <div className="flex items-center justify-center">
                {isLoading && (
                    <div className="py-6 mx-auto w-full">
                        <Loading />
                    </div>
                )}

                {!isLoading && (
                    <div className="mt-4">
                        <div>{t('provider-connections.stripe-widget-description')}</div>

                        <Button variant="primary" onClick={() => initialize()} className="mt-4">
                            {t('provider-connections.connect-with-stripe')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

ProviderConnectionConnectWithWidgetStripe.propTypes = {
    connectorId: PropTypes.string.isRequired,
    connectorCountryCode: PropTypes.string.isRequired,
    providerName: PropTypes.string.isRequired,
    providerLogoUrl: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    serviceProviderId: PropTypes.string.isRequired,
    onConnect: PropTypes.func.isRequired,
    providerConnectionId: PropTypes.string,
};
